"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelFieldVector = exports.ModelMutatorVector = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const geodesic_1 = require("../../../geo/geodesic");
const math_1 = require("../../../math");
const unit_1 = require("../../../unit");
const collection_field_1 = require("../collection.field");
class ModelMutatorVector extends collection_field_1.ModelMutator {
    manual(newValue) {
        if (newValue == null || (newValue.distance == null && newValue.bearing == null)) {
            this.state.setFieldValue(this.field.id, null);
            return;
        }
        // Force the vector to be between 0 and two Pi
        newValue.bearing = unit_1.IkeAngle.normalize(newValue.bearing);
        // Sometimes pitch is not set, It is a required field
        if (newValue.pitch == null || isNaN(newValue.pitch)) {
            newValue.pitch = 0;
        }
        this.state.setFieldValue(this.field.id, {
            provider: CollectionModelFieldVector.PROVIDER_MANUAL,
            value: newValue,
        });
    }
    p2p(state) {
        if (!Array.isArray(state)) {
            throw new Error(`Field ${this.field.id}: Invalid capture state.`);
        }
        if (state.length < 2) {
            throw new Error(`Field ${this.field.id}: Invalid capture count "${state.length}".`);
        }
        this.state.setFieldValue(this.field.id, {
            provider: CollectionModelFieldVector.PROVIDER_P2P,
            captures: state.slice(),
        });
    }
    collection(collectionId) {
        if (collectionId == null) {
            this.state.setFieldValue(this.field.id, null);
            return;
        }
        this.state.setFieldValue(this.field.id, {
            provider: CollectionModelFieldVector.PROVIDER_COLLECTION,
            collectionId,
        });
    }
    reverse() {
        if (!this.field.hasValue()) {
            return;
        }
        if (this.field.isPointToPoint()) {
            const captures = this.field.stateValue.captures.slice().reverse();
            this.p2p(captures);
            return;
        }
        if (this.field.isManual()) {
            const value = this.field.value;
            this.manual({
                distance: value.distance,
                bearing: unit_1.IkeAngle.flip(value.bearing),
                pitch: value.pitch,
            });
        }
    }
    copy(from) {
        if (from.isPointToPoint()) {
            this.p2p(from.stateValue.captures);
            return true;
        }
        if (from.isCollectionLink()) {
            const [targetCollection] = from.getCollections();
            // Dont link to ourself
            if (targetCollection == null) {
                return true;
            }
            if (targetCollection.collectionId === this.field.collection.id) {
                const otherCollection = from.collection;
                this.collection(otherCollection.id);
                return true;
            }
            this.collection(targetCollection.collectionId);
            return true;
        }
        if (from.isManual()) {
            this.manual(from.value);
            return true;
        }
        return false;
    }
}
exports.ModelMutatorVector = ModelMutatorVector;
class CollectionModelFieldVector extends collection_field_1.CollectionModelField {
    static isVectorField(f) {
        return f.schema.type === CollectionModelFieldVector.TYPE;
    }
    get Mutator() {
        if (this._Mutator == null) {
            this._Mutator = new ModelMutatorVector(this);
        }
        return this._Mutator;
    }
    get value() {
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return null;
        }
        if (stateValue.provider === CollectionModelFieldVector.PROVIDER_MANUAL) {
            return stateValue.value;
        }
        if (stateValue.provider === CollectionModelFieldVector.PROVIDER_P2P) {
            const vectorValue = math_1.MultiPoint.Points(this.getCaptures());
            // To be backwards compatible, vertical values should not have a sign on them
            vectorValue.vertical = Math.abs(vectorValue.vertical);
            return vectorValue;
        }
        if (stateValue.provider === CollectionModelFieldVector.PROVIDER_COLLECTION) {
            const collection = this.collection;
            const sourceLocation = collection.location;
            if (sourceLocation == null) {
                return null;
            }
            let targetLocation;
            // Try to get the current location state
            const targetCollectionModel = collection.state.getCollectionModel(stateValue.collectionId);
            if (targetCollectionModel == null) {
                // If the current state is not available, then get the DB stored location data
                const targetCollection = collection.state.getCollection(stateValue.collectionId);
                if (targetCollection == null) {
                    return null;
                }
                targetLocation = core_api_1.ParseJSON.fromGeoPoint(targetCollection.location);
            }
            else {
                targetLocation = targetCollectionModel.location;
            }
            if (targetLocation == null) {
                return null;
            }
            return geodesic_1.IkeGeodesic.getVector(sourceLocation, targetLocation);
        }
        throw new Error(`Field ${this.id}: Invalid Provider type "${stateValue['provider']}"`);
    }
    get provider() {
        const stateValue = this.stateValue;
        if (stateValue == null || stateValue.provider == null) {
            return CollectionModelFieldVector.PROVIDER_MANUAL;
        }
        return stateValue.provider;
    }
    isManual() {
        return this.provider === CollectionModelFieldVector.PROVIDER_MANUAL;
    }
    isPointToPoint() {
        return this.provider === CollectionModelFieldVector.PROVIDER_P2P;
    }
    isCollectionLink() {
        return this.provider === CollectionModelFieldVector.PROVIDER_COLLECTION;
    }
    getOwnedCaptures() {
        return this.getCaptures();
    }
    getCaptures() {
        const captures = [];
        const captureIds = this.getCaptureIds();
        for (const captureId of captureIds) {
            const capture = this.collection.state.getCapture(captureId);
            if (capture == null) {
                return [];
            }
            if (capture.orientation == null) {
                throw new Error(`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing orientation)`);
            }
            if (capture.calibrationid == null) {
                throw new Error(`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing calibrationId)`);
            }
            captures.push(capture);
        }
        return captures;
    }
    getOwnedCaptureIds() {
        return this.getCaptureIds();
    }
    getCaptureIds() {
        if (!this.isPointToPoint()) {
            return [];
        }
        return this.stateValue.captures;
    }
    getTargetModel() {
        if (!this.isCollectionLink()) {
            return null;
        }
        return this.collection.state.getCollectionModel(this.stateValue.collectionId);
    }
    getTargetModelAsync() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!this.isCollectionLink()) {
                return null;
            }
            return this.collection.state.getCollectionModelAsync(this.stateValue.collectionId);
        });
    }
    getTargetJson() {
        if (!this.isCollectionLink()) {
            return null;
        }
        return this.collection.state.getCollection(this.stateValue.collectionId);
    }
    /**
     * @deprecated Use getTargetJson or getTargetModel instead
     */
    getCollections() {
        // There is no reason for this to be an array result when you can only link to one collection
        return this.getCollectionIds().map((c) => this.collection.state.getCollection(c));
    }
    /**
     * @deprecated Use getTargetJson or getTargetModel instead
     */
    getCollectionIds() {
        // There is no reason for this to be an array result when you can only link to one collection
        if (!this.isCollectionLink()) {
            return [];
        }
        return [this.stateValue.collectionId];
    }
    hasValue() {
        return this.value != null;
    }
    /**
     * An async method which is guaranteed to load the linked CollectionModel.
     * Can be used to force the linked collection to load before using `value` or `hasValue`.
     */
    getLinkedCollection() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a;
            if (((_a = this.stateValue) === null || _a === void 0 ? void 0 : _a.provider) !== CollectionModelFieldVector.PROVIDER_COLLECTION) {
                return null;
            }
            return yield this.collection.state.getCollectionModelAsync(this.stateValue.collectionId);
        });
    }
}
exports.CollectionModelFieldVector = CollectionModelFieldVector;
CollectionModelFieldVector.PROVIDER_MANUAL = 'manual';
CollectionModelFieldVector.PROVIDER_P2P = 'pointtopoint';
CollectionModelFieldVector.PROVIDER_COLLECTION = 'collection';
CollectionModelFieldVector.TYPE = core_api_1.IkeFieldType.VECTOR;
//# sourceMappingURL=vector.field.js.map